import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { AccountRole } from 'types/account_role';
import { OrganizationInvitation } from 'types/invitation';

import { useAPI } from 'hooks/api/useAPI';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';

export type CreateOrganizationInvitationParams = {
  email: string;
  roles: AccountRole[];
};

/** Invite user to an organization */
export function useCreateOrganizationInvitation({
  onMutate,
  onSettled,
  ...options
}: UseMutationOptions<
  OrganizationInvitation,
  APIError,
  CreateOrganizationInvitationParams
> = {}) {
  const queryClient = useQueryClient();
  const { client } = useAPI();

  const { data: organization } = useCurrentOrganization();
  const organizationId = organization?.id;

  return useMutation(
    async (params) => {
      if (!organizationId) {
        return Promise.reject(new Error('No organization id provided.'));
      }

      const { data } = await client.post<OrganizationInvitation>(
        `/internal/orgs/${organizationId}/invitations`,
        params
      );
      return data;
    },
    {
      ...options,
      onSettled(...args) {
        onSettled?.(...args);

        queryClient.invalidateQueries(['organization-invitations']);
      },
    }
  );
}
